import { supabase } from './supabaseClient.js';

/**
 * Servicio para gestión de pagos usando la tabla 'pagos' de Supabase
 */
export class PaymentsService {
  /**
   * Obtener todos los pagos con filtros opcionales
   */
  static async getPayments(filters = {}) {
    try {
      let query = supabase
        .from('pagos')
        .select(`
          *,
          contratacion:contrataciones(
            id,
            nombre,
            mail,
            rut,
            estado,
            datos_titular,
            rut_titular,
            created_at,
            cotizaciones:cotizacion_id(
              id,
              planes:plan_id(
                nombre,
                uf_base
              )
            )
          ),
          orden_pago:ordenes_pago(
            id,
            monto,
            estado,
            created_at
          ),
          certificados:certificados!pago_id(
            id,
            fecha_emision,
            archivo_id
          )
        `);

      // Aplicar filtros usando los campos reales de la tabla
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.contratacion_id) {
        query = query.eq('contratacion_id', filters.contratacion_id);
      }
      if (filters.date_from) {
        query = query.gte('transaction_date', filters.date_from);
      }
      if (filters.date_to) {
        query = query.lte('transaction_date', filters.date_to);
      }
      if (filters.payment_type_code) {
        query = query.eq('payment_type_code', filters.payment_type_code);
      }
      if (filters.is_recurring !== undefined) {
        query = query.eq('is_recurring', filters.is_recurring);
      }

      const { data, error } = await query.order('transaction_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw new Error(`Error al cargar los pagos: ${error.message}`);
      }
      return data || [];
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw new Error('Error al cargar los pagos');
    }
  }

  /**
   * Obtener un pago por ID con todas las relaciones
   */
  static async getPaymentById(id) {
    try {
      const { data, error } = await supabase
        .from('pagos')
        .select(`
          *,
          contratacion:contrataciones(
            id,
            nombre,
            mail,
            rut,
            estado,
            datos_titular,
            rut_titular,
            created_at,
            expires_at,
            cotizaciones:cotizacion_id(
              id,
              planes:plan_id(
                nombre,
                uf_base,
                descripcion
              ),
              cotizacion_lineas(
                precio_uf,
                descuento_aplicado,
                coberturas:cobertura_id(
                  nombre,
                  descripcion
                )
              )
            ),
            contratacion_cargas(
              id,
              nombre,
              apellido_paterno,
              apellido_materno,
              rut,
              tipo,
              edad
            )
          ),
          orden_pago:ordenes_pago(
            id,
            monto,
            estado,
            created_at,
            updated_at,
            descripcion
          ),
          certificados:certificados!pago_id(
            id,
            fecha_emision,
            archivo_id
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching payment:', error);
      throw error;
    }
  }

  /**
   * Crear un nuevo pago
   */
  static async createPayment(paymentData) {
    try {
      // Validar campos requeridos según esquema de tabla 'pagos'
      const requiredFields = ['session_id', 'amount', 'status', 'payment_type_code'];
      for (const field of requiredFields) {
        if (!paymentData[field]) {
          throw new Error(`Campo requerido faltante: ${field}`);
        }
      }

      const { data, error } = await supabase
        .from('pagos')
        .insert([{
          session_id: paymentData.session_id,
          amount: paymentData.amount,
          status: paymentData.status,
          authorization_code: paymentData.authorization_code,
          payment_type_code: paymentData.payment_type_code,
          transaction_date: paymentData.transaction_date || new Date().toISOString(),
          contratacion_id: paymentData.contratacion_id,
          orden_pago_id: paymentData.orden_pago_id,
          is_recurring: paymentData.is_recurring || false,
          patpass_voucher: paymentData.patpass_voucher,
          next_payment_date: paymentData.next_payment_date
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  }

  /**
   * Actualizar un pago
   */
  static async updatePayment(id, updates) {
    try {
      const { data, error } = await supabase
        .from('pagos')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating payment:', error);
      throw error;
    }
  }

  /**
   * Procesar reembolso
   */
  static async processRefund(paymentId, refundAmount, reason) {
    try {
      const { data, error } = await supabase
        .from('pagos')
        .update({
          status: 'refunded',
          // Nota: Los campos de reembolso no están en el esquema actual de 'pagos'
          // Se podría agregar un campo JSON para metadata adicional
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error processing refund:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de pagos
   */
  static async getPaymentStats(dateRange = {}) {
    try {
      let query = supabase
        .from('pagos')
        .select('amount, status, transaction_date, payment_type_code, is_recurring');

      if (dateRange.from) {
        query = query.gte('transaction_date', dateRange.from);
      }
      if (dateRange.to) {
        query = query.lte('transaction_date', dateRange.to);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calcular estadísticas
      const stats = {
        total_payments: data.length,
        total_amount: data.reduce((sum, payment) => sum + (payment.amount || 0), 0),
        successful_payments: data.filter(p => p.status === 'completed' || p.status === 'approved').length,
        failed_payments: data.filter(p => p.status === 'failed' || p.status === 'rejected').length,
        pending_payments: data.filter(p => p.status === 'pending').length,
        refunded_payments: data.filter(p => p.status === 'refunded').length,
        recurring_payments: data.filter(p => p.is_recurring === true).length,
        payment_types: data.reduce((acc, payment) => {
          acc[payment.payment_type_code] = (acc[payment.payment_type_code] || 0) + 1;
          return acc;
        }, {})
      };

      return stats;
    } catch (error) {
      console.error('Error fetching payment stats:', error);
      throw error;
    }
  }
}

export default PaymentsService;